import React from 'react';
import EntityBrowserDialog from './EntityBrowserDialog';
import { EnumeratedSetManualMemberBlockedModal } from './EnumeratedSetManualMemberBlockedModal';
import { getEntityValueFromARef, normalizeKey } from '../../types';

type RefEntitySummary = {
    Id: string;
    DisplayName?: string;
    Name?: string;
    EntityType?: string;
};

export interface EntityUpsertReferenceBrowsersProps {
    multiRefBrowserField: string | null;
    onCloseMultiRef: () => void;
    deferredArrayAddField: string | null;
    onCloseDeferredAdd: () => void;
    schemaAttributes: any[];
    formData: Record<string, unknown>;
    isSet: boolean;
    manualMemberAllowedTypes: string[] | null;
    onMultiRefConfirm: (field: string, nextRefs: Array<{ Id: string; DisplayName?: string; EntityType?: string }>) => void;
    onDeferredAddConfirm: (field: string, toAdd: Array<{ Id: string; DisplayName?: string; EntityType?: string }>) => void;
}

function resolveRefToTypes(
    fieldName: string,
    attr: any,
    isSet: boolean,
    formData: Record<string, unknown>,
    manualMemberAllowedTypes: string[] | null
): string[] {
    const baseRefToTypes =
        attr?.isReferenceTo && Array.isArray(attr.isReferenceTo) && attr.isReferenceTo.length > 0
            ? attr.isReferenceTo.map((ent: any) => (typeof ent === 'string' ? ent : ent?.DisplayName ?? ent?.Id ?? ent))
            : (attr?.Entities ?? attr?.entities ?? [])
                  .map((ent: any) => (typeof ent === 'string' ? ent : ent?.DisplayName ?? ent?.Id ?? ent))
                  .filter(Boolean);
    const filterScopeVal = formData && getEntityValueFromARef('FilterScope', formData);
    if (fieldName === 'ManualSetMember' && isSet) {
        return manualMemberAllowedTypes !== null ? manualMemberAllowedTypes : filterScopeVal ? [String(filterScopeVal)] : [];
    }
    return baseRefToTypes;
}

function isEnumeratedManualSetMember(fieldName: string, isSet: boolean, formData: Record<string, unknown>): boolean {
    if (!isSet || fieldName !== 'ManualSetMember') return false;
    const setTypeLower = (getEntityValueFromARef('Type', formData ?? {}) ?? '').toString().trim().toLowerCase();
    return setTypeLower === 'enumerated';
}

/** Multi-ref and deferred-add entity browser overlays for GenericEntityUpsertDialog. */
export const EntityUpsertReferenceBrowsers: React.FC<EntityUpsertReferenceBrowsersProps> = ({
    multiRefBrowserField,
    onCloseMultiRef,
    deferredArrayAddField,
    onCloseDeferredAdd,
    schemaAttributes,
    formData,
    isSet,
    manualMemberAllowedTypes,
    onMultiRefConfirm,
    onDeferredAddConfirm,
}) => (
    <>
        {multiRefBrowserField != null && (() => {
            if (isEnumeratedManualSetMember(multiRefBrowserField, isSet, formData)) {
                return <EnumeratedSetManualMemberBlockedModal open onClose={onCloseMultiRef} />;
            }
            const attr = schemaAttributes?.find((a: any) => (a.name || a.id) === multiRefBrowserField);
            const refToTypes = resolveRefToTypes(multiRefBrowserField, attr, isSet, formData, manualMemberAllowedTypes);
            const value = getEntityValueFromARef(multiRefBrowserField, formData) ?? [];
            const refItems = (Array.isArray(value) ? value : value ? [value] : [])
                .map((item: any) => {
                    if (item == null) return { id: '', display: '' };
                    if (typeof item === 'string') return { id: item, display: item };
                    return {
                        id: item.Id ?? item.id ?? '',
                        display: item.DisplayName ?? item.displayName ?? item.Id ?? item.id ?? '',
                        EntityType: item.EntityType ?? item.entityType,
                    };
                })
                .filter((r: { id: string }) => r.id);
            const handleMultiRefConfirm = (
                first: Array<{ Id: string; DisplayName?: string; Name?: string; EntityType?: string }> | string[],
                _effectiveEntityType?: string,
                selectedEntities?: RefEntitySummary[]
            ) => {
                const existingIds = new Set(refItems.map((r: { id: string }) => r.id));
                const isEntityArray =
                    Array.isArray(first) && first.length > 0 && typeof first[0] === 'object' && first[0] !== null && 'Id' in first[0];
                const idsToAdd = isEntityArray
                    ? (first as Array<{ Id: string }>).map((e) => e.Id).filter((id) => !existingIds.has(id))
                    : (first as string[]).filter((id) => !existingIds.has(id));
                const entityList: RefEntitySummary[] = isEntityArray
                    ? (first as RefEntitySummary[])
                    : (selectedEntities ?? []);
                const entityMap = new Map(entityList.map((e) => [e.Id, e]));
                const nextRefs = [
                    ...refItems.map(
                        (r: { id: string; display?: string; displayName?: string; EntityType?: string; entityType?: string }) => ({
                            Id: r.id,
                            DisplayName: r.display ?? r.displayName ?? r.id,
                            ...((r.EntityType ?? r.entityType) && { EntityType: r.EntityType ?? r.entityType }),
                        })
                    ),
                    ...idsToAdd.map((id) => {
                        const entity = entityMap.get(id);
                        return {
                            Id: id,
                            DisplayName: entity?.DisplayName ?? entity?.Name,
                            EntityType: entity?.EntityType,
                        };
                    }),
                ];
                onMultiRefConfirm(multiRefBrowserField, nextRefs);
            };
            const label = attr?.label ?? multiRefBrowserField;
            return refToTypes.length > 0 ? (
                <EntityBrowserDialog
                    key={multiRefBrowserField}
                    open
                    onClose={onCloseMultiRef}
                    onConfirm={handleMultiRefConfirm}
                    entityType={refToTypes[0]}
                    entityTypes={refToTypes.length > 1 ? refToTypes : undefined}
                    title={`Select ${label}`}
                    callerName={`GenericEntityUpsertDialog (field: ${multiRefBrowserField})`}
                    singleSelection={false}
                    alreadySelectedEntities={refItems.map(
                        (r: { id: string; display?: string; displayName?: string; EntityType?: string; entityType?: string }) => ({
                            Id: r.id,
                            DisplayName: r.display ?? r.displayName ?? r.id,
                            ...((r.EntityType ?? r.entityType) && { EntityType: r.EntityType ?? r.entityType }),
                        })
                    )}
                    modalSize="70%"
                />
            ) : null;
        })()}

        {deferredArrayAddField != null && (() => {
            if (isEnumeratedManualSetMember(deferredArrayAddField, isSet, formData)) {
                return <EnumeratedSetManualMemberBlockedModal open onClose={onCloseDeferredAdd} />;
            }
            const attr = schemaAttributes?.find(
                (a: any) => normalizeKey((a.name || a.id) ?? '') === normalizeKey(deferredArrayAddField ?? '')
            );
            const refToTypes = resolveRefToTypes(deferredArrayAddField, attr, isSet, formData, manualMemberAllowedTypes);
            const handleDeferredAddConfirm = (
                selectedEntities: Array<{ Id: string; DisplayName: string; EntityType: string }>
            ) => {
                const toAdd = selectedEntities.map((e) => ({
                    Id: e.Id,
                    DisplayName: e.DisplayName,
                    EntityType: e.EntityType,
                }));
                if (toAdd.length > 0) {
                    onDeferredAddConfirm(deferredArrayAddField, toAdd);
                }
            };
            const label = attr?.label ?? attr?.name ?? deferredArrayAddField;
            return refToTypes.length > 0 ? (
                <EntityBrowserDialog
                    key={deferredArrayAddField}
                    open
                    onClose={onCloseDeferredAdd}
                    onConfirm={handleDeferredAddConfirm}
                    entityType={refToTypes[0]}
                    entityTypes={refToTypes.length > 1 ? refToTypes : undefined}
                    title={`Add items — ${label}`}
                    callerName={`GenericEntityUpsertDialog (deferred add: ${deferredArrayAddField})`}
                    singleSelection={false}
                    alreadySelectedEntities={[]}
                    modalSize="90%"
                />
            ) : null;
        })()}
    </>
);
